import { useState } from "react";
import { makeStyles } from "@mui/styles";
import PropTypes from "prop-types";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  Box,
  Typography,
  TextField,
  Divider,
  FormControl,
  FormControlLabel,
  Checkbox,
  FormHelperText,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import SlideUpTransition from "../fundamentals/SlideUpTransition";
import ConfirmButton from "../fundamentals/ConfirmButton";
import { getFormattedPhoneNumber } from "../../util/commonUtils";
import { validatePhoneNumber } from "../../util/inputValidation";
import { INVALID_PHONE_NUMBER, PHONE_REQUIRED } from "../../util/messageConstants";
import ScheduledHoursSegment from "./ScheduledHoursSegment";
import {
  CALLOUT_OT_HOURS_PREFERENCES_LIST,
  CALLOUT_OT_TIME_OF_DAY_PREFERENCES_LIST,
  CONTINUOUS_WITH_PREFERENCES_IAM,
  MUST_SELECT_AT_LEAST_ONE_TIME_OF_DAY_PREFERENCE_ERROR,
  OT_ELIGIBILITY_EMPLOYEE_WARNING,
  SCHEDULED_DAYS_OFF_AT_LEAST_TWO_ERROR,
} from "../../util/overtime/constants";
import { CLASSIFICATION_SENIORITY_LIST_TYPE, IAM_CONTRACT, WEEKDAYS_SHORT } from "../../util/constants";
import {
  isCalloutSectionPartiallyFilled,
  validateCalloutSection,
  validateCalloutSectionDuplication,
  validateContinuousWithOrCalloutPreferenceSelected,
  validateShiftSegments,
} from "../../util/overtime/inputValidation";
import { getLatestPubSLEvent } from "../../api/seniority";
import { selectUserSession } from "../../slices/userSession/userSessionSlice";
import { useSelector } from "react-redux";
import ConfirmationDialog from "../fundamentals/ConfirmationDialog";
import SuccessDialog from "../fundamentals/SuccessDialog";
import { buildVoluntaryOTSignupPayload } from "../../util/overtime/overtimeUtils";
import { addOvertimeEvent, isOvertimeEventEligible } from "../../api/overtime";

const useStyles = makeStyles((theme) => ({
  button: {
    width: "190px",
    float: "right",
    fontWeight: "bold",
  },
  mobileButton: {
    width: "100%",
    fontWeight: "bold",
    paddingTop: 10,
    paddingBottom: 10,
    paddingLeft: 0,
    paddingRight: 0,
    borderRadius: 5,
  },
  sectionTitleFontStyle: {
    fontSize: "16px",
    fontWeight: "bold",
    float: "left",
    marginTop: "10px",
    marginRight: "10px",
    color: "black",
  },
  sectionTitleDescriptionFontStyle: { fontStyle: "italic", fontSize: "16px", marginTop: "10px" },
  sectionSubtitleFontStyle: {
    fontSize: "14px",
    fontWeight: "bold",
    float: "left",
    marginTop: "10px",
    marginRight: "10px",
    color: theme.palette.secondary.swaBlue,
  },
  listFontStyle: {
    fontSize: "14px",
    float: "left",
    marginTop: "10px",
    marginRight: "10px",
    color: theme.palette.secondary.swaBlue,
  },
  sectionBox: {
    display: "inline-block",
    width: "100%",
  },
}));

const CreateVoluntaryOTSignupIAM = (props) => {
  //#region Constants and State
  const classes = useStyles();
  const userSession = useSelector(selectUserSession);
  const [isSignupDialogOpen, setIsSignupDialogOpen] = useState(false);
  const userContract = IAM_CONTRACT;
  //Seniority Date section
  const [seniorityDate, setSeniorityDate] = useState("");
  const [isSeniorityDateLoading, setIsSeniorityDateLoading] = useState(false);
  //Phone Number section
  const [phoneNumber, setPhoneNumber] = useState("");
  const [phoneNumberErrors, setPhoneNumberErrors] = useState("");
  //Scheduled Hours section
  const [shiftOffChecked, setShiftOffChecked] = useState(false);
  const shiftObjectInitialState = {
    shiftStartHr: "",
    shiftStartMin: "",
    shiftEndHr: "",
    shiftEndMin: "",
    shiftSegmentError: "",
  };
  const [shifts, setShifts] = useState([shiftObjectInitialState]);
  //Scheduled Days Off section
  const offDaysCheckedInitialState = Object.fromEntries(WEEKDAYS_SHORT.map((day) => [day, false]));
  const [offDaysChecked, setOffDaysChecked] = useState(offDaysCheckedInitialState);
  const [scheduledDaysOffErrors, setScheduledDaysOffErrors] = useState("");
  //Continuous With Preferences section
  const [continuousWith, setContinuousWith] = useState("");
  const [continuousWithError, setContinuousWithError] = useState("");
  const [continuousWithTouchingChecked, setContinuousWithTouchingChecked] = useState(false);
  //Callout OT Preferences section
  const calloutHoursInitialState = CALLOUT_OT_HOURS_PREFERENCES_LIST.map(() => {
    return { preference: "", error: "" };
  });
  const [calloutHours, setCalloutHours] = useState(calloutHoursInitialState);
  const calloutTimeOfDayInitialState = CALLOUT_OT_TIME_OF_DAY_PREFERENCES_LIST.map(() => {
    return { preference: "", touchingShift: false, error: "" };
  });
  const [calloutTimeOfDayPreferences, setCalloutTimeOfDayPreferences] = useState(calloutTimeOfDayInitialState);
  //Bottom Checkboxes section
  const [willAcceptChecked, setWillAcceptChecked] = useState(false);
  const [waive45MinChecked, setWaive45MinChecked] = useState(false);
  //Confirmation Dialogs section
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [isSubmitConfirmationDialogOpen, setIsSubmitConfirmationDialogOpen] = useState(false);
  const [isSubmitSuccessDialogOpen, setIsSubmitSuccessDialogOpen] = useState(false);
  const [submitErrorMessage, setSubmitErrorMessage] = useState("");
  //#endregion

  //#region API Functions
  const getSeniorityDate = async () => {
    try {
      setIsSeniorityDateLoading(true);
      const response = await getLatestPubSLEvent(props.station, props.workgroup, CLASSIFICATION_SENIORITY_LIST_TYPE);
      const data = await response.json();
      const empSlRecord = data?.seniorityList?.find((item) => item?.eid === userSession.eid);
      if (empSlRecord) {
        setSeniorityDate(empSlRecord.seniorityDate);
      } else {
        setSeniorityDate("N/A");
      }
    } catch (e) {
      console.error("Error fetching seniority date:", e);
    } finally {
      setIsSeniorityDateLoading(false);
    }
  };

  const isOTEventEligibleForSignup = async () => {
    let isEligible = false;
    let error = "";
    try {
      setIsSubmitLoading(true);
      const result = await isOvertimeEventEligible(userSession.eid, props.station, props.selectedDate.toISO());
      isEligible = result.status === 200;

      if (result.status === 400) {
        error = OT_ELIGIBILITY_EMPLOYEE_WARNING;
      } else if (result.status === 500) {
        error = "Failed to process request. Please try again later.";
      }
    } catch (e) {
      console.error("Error fetching event eligibility:", e);
      error = "Failed to process request. Please try again later.";
      isEligible = false;
    } finally {
      setSubmitErrorMessage(error);
      setIsSubmitLoading(false);
      setIsSubmitSuccessDialogOpen(!isEligible);
    }
    return isEligible;
  };

  const addOvertimeSignupEvent = async (payload) => {
    let error = "";
    try {
      setIsSubmitLoading(true);
      const response = await addOvertimeEvent(payload);
      if (response.status !== 200) {
        error = "Failed to submit Voluntary OT Signup. Please try again later.";
      }
    } catch (e) {
      console.error("Error submitting signup:", e);
      error = "Failed to submit Voluntary OT Signup. Please try again later.";
    } finally {
      setSubmitErrorMessage(error);
      setIsSubmitLoading(false);
      setIsSubmitSuccessDialogOpen(true);
    }
  };
  //#endregion

  //#region Validation Functions
  const validateContactNumber = () => {
    let isFormValid = false;
    const phoneNumberValidation = validatePhoneNumber(phoneNumber, PHONE_REQUIRED, INVALID_PHONE_NUMBER);
    setPhoneNumberErrors(phoneNumberValidation);
    if (phoneNumberValidation === "" && phoneNumber !== "") {
      const formattedPhoneNumber = getFormattedPhoneNumber(phoneNumber);
      setPhoneNumber(formattedPhoneNumber);
      isFormValid = true;
    }
    return isFormValid;
  };

  const validateScheduledDaysOff = () => {
    const scheduledDaysOffSelected = Object.values(offDaysChecked).filter((day) => day);
    if (scheduledDaysOffSelected.length < 2) {
      setScheduledDaysOffErrors(SCHEDULED_DAYS_OFF_AT_LEAST_TWO_ERROR);
      return false;
    } else {
      setScheduledDaysOffErrors("");
      return true;
    }
  };

  const validateContinuousWithAndCalloutSections = () => {
    //Continuous With Preferences and Callout OT Preferences section (either can be optional, but at least one must be selected)
    let isContinuousWithAndCalloutPreferencesValid = true;
    const continuousWithError = validateContinuousWithOrCalloutPreferenceSelected(
      continuousWith,
      calloutHours,
      calloutTimeOfDayPreferences
    );

    if (continuousWithError !== "") {
      setContinuousWithError(continuousWithError);
      updateCalloutPreferencesErrors(calloutHours, continuousWithError, setCalloutHours);
      updateCalloutPreferencesErrors(calloutTimeOfDayPreferences, continuousWithError, setCalloutTimeOfDayPreferences);
      isContinuousWithAndCalloutPreferencesValid = false;
    } else if (
      continuousWith === "" ||
      (continuousWith !== "" && isCalloutSectionPartiallyFilled(calloutHours, calloutTimeOfDayPreferences))
    ) {
      setContinuousWithError("");
      const calloutSectionError = validateCalloutSection(calloutHours, calloutTimeOfDayPreferences);
      isContinuousWithAndCalloutPreferencesValid = calloutSectionError === "";
      if (calloutSectionError === MUST_SELECT_AT_LEAST_ONE_TIME_OF_DAY_PREFERENCE_ERROR) {
        updateCalloutPreferencesErrors(
          calloutTimeOfDayPreferences,
          calloutSectionError,
          setCalloutTimeOfDayPreferences
        );
        updateCalloutPreferencesErrors(calloutHours, "", setCalloutHours);
      } else {
        updateCalloutPreferencesErrors(calloutHours, calloutSectionError, setCalloutHours);
        updateCalloutPreferencesErrors(calloutTimeOfDayPreferences, "", setCalloutTimeOfDayPreferences);
      }
    } else if (
      calloutHours.some((pref) => pref.error !== "" || calloutTimeOfDayPreferences.some((pref) => pref.error !== ""))
    ) {
      //section had errors already
      isContinuousWithAndCalloutPreferencesValid = false;
    } else {
      //clear errors
      setContinuousWithError("");
      updateCalloutPreferencesErrors(calloutHours, "", setCalloutHours);
      updateCalloutPreferencesErrors(calloutTimeOfDayPreferences, "", setCalloutTimeOfDayPreferences);
    }

    if (isContinuousWithAndCalloutPreferencesValid) {
      //Validate duplication to ensure no duplicates exist on submit
      let updatedCalloutHours = [...calloutHours];
      updatedCalloutHours = validateCalloutSectionDuplication(updatedCalloutHours);
      let updatedCalloutTimeOfDayPreferences = [...calloutTimeOfDayPreferences];
      updatedCalloutTimeOfDayPreferences = validateCalloutSectionDuplication(updatedCalloutTimeOfDayPreferences);
      setCalloutHours(updatedCalloutHours);
      setCalloutTimeOfDayPreferences(updatedCalloutTimeOfDayPreferences);

      if (
        updatedCalloutHours.some((pref) => pref.error !== "") ||
        updatedCalloutTimeOfDayPreferences.some((pref) => pref.error !== "")
      ) {
        isContinuousWithAndCalloutPreferencesValid = false;
      }
    }

    return isContinuousWithAndCalloutPreferencesValid;
  };

  const validateSignupForm = () => {
    //Phone Number section
    const isPhoneFormValid = validateContactNumber();

    //Scheduled Hours section
    let isShiftFormValid = true;
    if (!shiftOffChecked) {
      const { segments, isFormValid } = validateShiftSegments(shifts, props.selectedDate, props.station, userContract);
      isShiftFormValid = isFormValid;
      setShifts(segments);
    }

    //Scheduled Days Off section
    const isScheduledDaysOffValid = validateScheduledDaysOff();

    //Continuous With Preferences and Callout OT Preferences section
    const isContinuousWithAndCalloutPreferencesValid = validateContinuousWithAndCalloutSections();

    return (
      isPhoneFormValid && isShiftFormValid && isScheduledDaysOffValid && isContinuousWithAndCalloutPreferencesValid
    );
  };

  const resetForm = () => {
    //Phone Number section
    setPhoneNumber("");
    setPhoneNumberErrors("");

    //Scheduled Hours section
    setShiftOffChecked(false);
    setShifts([shiftObjectInitialState]);

    //Scheduled Days Off section
    setOffDaysChecked(offDaysCheckedInitialState);
    setScheduledDaysOffErrors("");

    //Continuous With and Callout OT Preferences sections
    resetPreferencesSections();

    //Bottom Checkboxes section
    setWillAcceptChecked(false);
    setWaive45MinChecked(false);

    //Dialogs section
    setSubmitErrorMessage("");
  };

  const resetPreferencesSections = () => {
    //Continuous With Preferences section
    setContinuousWith("");
    setContinuousWithTouchingChecked(false);
    setContinuousWithError("");

    //Callout OT Preferences section
    setCalloutHours(calloutHoursInitialState);
    setCalloutTimeOfDayPreferences(calloutTimeOfDayInitialState);
  };

  const updateCalloutPreferencesErrors = (preferences, errorMessage, preferencesSetterFunc) => {
    const copiedPreferences = [...preferences];
    copiedPreferences.forEach((preference) => {
      if (preference.error === "" || errorMessage === "") preference.error = errorMessage;
    });
    preferencesSetterFunc(copiedPreferences);
  };
  //#endregion

  //#region Dialog Handlers
  const handleClickDialogOpen = async () => {
    setIsSignupDialogOpen(true);
    await getSeniorityDate();
  };

  const handleClickDialogClose = (event, reason) => {
    if (reason && reason === "backdropClick") return;
    setIsSignupDialogOpen(false);
    resetForm();
  };
  //#endregion

  //#region Phone section handlers
  const handlePhoneNumberChange = (event) => {
    setPhoneNumber(event.target.value);
  };

  const handlePhoneNumberBlur = () => {
    validateContactNumber();
  };
  //#endregion

  //#region Scheduled Hours section handlers
  const handleScheduledHoursOffCheckedChange = (event) => {
    setShiftOffChecked(event.target.checked);
    if (event.target.checked) {
      setShifts([shiftObjectInitialState]);
    }
  };

  const handleAddShiftSegment = () => {
    const existingSegments = [...shifts];
    setShifts([...existingSegments, shiftObjectInitialState]);
    setTimeout(() => {
      const rowElem = document.querySelector(`[data-id="${shifts.length + 1}"]`);
      if (rowElem) {
        rowElem.scrollIntoView({ behavior: "smooth", block: "nearest" });
      }
    }, 0);
  };
  //#endregion

  //#region Scheduled Days Off section handlers
  const handleScheduledDaysOffCheckedChange = (event, day) => {
    const newOffDays = { ...offDaysChecked };
    newOffDays[day] = event.target.checked;
    setOffDaysChecked(newOffDays);
    setScheduledDaysOffErrors("");
  };
  //#endregion

  //#region Continuous With Preferences Handlers
  const handleContinuousWithPreferenceChange = (event) => {
    setContinuousWith(event.target.value);
    setContinuousWithError("");
    updateCalloutPreferencesErrors(calloutHours, "", setCalloutHours);
    updateCalloutPreferencesErrors(calloutTimeOfDayPreferences, "", setCalloutTimeOfDayPreferences);
  };
  //#endregion

  //region Callout OT Preferences Handlers
  const handleCalloutHourPreferenceChange = (event, index) => {
    // Clear any existing errors
    let updatedPreferences = [...calloutHours];
    updateCalloutPreferencesErrors(updatedPreferences, "", setCalloutHours);
    updateCalloutPreferencesErrors(calloutTimeOfDayPreferences, "", setCalloutTimeOfDayPreferences);
    setContinuousWithError("");

    updatedPreferences[index].preference = event.target.value;
    updatedPreferences = validateCalloutSectionDuplication(updatedPreferences);
    setCalloutHours(updatedPreferences);
  };

  const handleCalloutTimeOfDayPreferenceChange = (event, index) => {
    // Clear any existing errors
    let updatedPreferences = [...calloutTimeOfDayPreferences];
    updateCalloutPreferencesErrors(updatedPreferences, "", setCalloutTimeOfDayPreferences);
    updateCalloutPreferencesErrors(calloutHours, "", setCalloutHours);
    setContinuousWithError("");

    updatedPreferences[index].preference = event.target.value;
    updatedPreferences = validateCalloutSectionDuplication(updatedPreferences);

    setCalloutTimeOfDayPreferences(updatedPreferences);
  };

  const handleCalloutTimeOfDayTouchingShiftCheckedChange = (event, index) => {
    const updatedPreferences = [...calloutTimeOfDayPreferences];
    updatedPreferences[index].touchingShift = event.target.checked;
    setCalloutTimeOfDayPreferences(updatedPreferences);
  };

  const handleOnSubmitClick = async () => {
    const isFormValid = validateSignupForm();

    if (isFormValid) {
      const isEligible = await isOTEventEligibleForSignup();
      setIsSubmitConfirmationDialogOpen(isEligible);
    }
  };

  const handleSubmitYesClicked = async () => {
    const signupData = {
      date: props.selectedDate,
      station: props.station,
      workgroup: props.workgroup,
      phoneNumber: phoneNumber,
      isShiftOff: shiftOffChecked,
      shifts: shifts,
      offDaysChecked: offDaysChecked,
      continuousWith: continuousWith,
      continuousWithTouching: continuousWithTouchingChecked,
      calloutHours: calloutHours,
      calloutTimeOfDayPreferences: calloutTimeOfDayPreferences,
      willAccept: willAcceptChecked,
      waive45Min: waive45MinChecked,
    };

    const payload = buildVoluntaryOTSignupPayload(userSession, userContract, signupData);
    await addOvertimeSignupEvent(payload);
  };
  //#endregion

  //#region Component Renders
  const renderPhoneNumberSection = () => {
    return (
      <Grid container>
        <Grid item xs={12}>
          <Box className={classes.sectionBox}>
            <Typography className={classes.sectionTitleFontStyle}>Phone #</Typography>
            <TextField
              value={phoneNumber}
              variant="outlined"
              label="Enter Phone #"
              onChange={handlePhoneNumberChange}
              onBlur={handlePhoneNumberBlur}
              error={phoneNumberErrors !== ""}
              size="small"
              helperText={phoneNumberErrors}
            />
          </Box>
        </Grid>
      </Grid>
    );
  };

  const renderScheduledHoursSection = () => {
    return (
      <Grid container>
        <Grid item xs={10}>
          <Typography className={classes.sectionTitleFontStyle}>Scheduled Hours</Typography>
        </Grid>
        <Grid item xs={2}>
          <Box sx={{ position: "relative" }}>
            <FormControl variant="outlined">
              <FormControlLabel
                data-testid="shiftOffCheckbox"
                checked={shiftOffChecked}
                onChange={handleScheduledHoursOffCheckedChange}
                control={<Checkbox />}
                label="OFF"
              />
            </FormControl>
          </Box>
        </Grid>
        <Grid item xs={12}>
          <ScheduledHoursSegment
            shiftSegments={shifts}
            scheduledHoursOffChecked={shiftOffChecked}
            setShiftSegments={setShifts}
          />
        </Grid>
        <Grid container justifyContent="center">
          <Grid item xs={12} sx={{ display: "flex", justifyContent: "center" }}>
            <Button
              className={classes.button}
              variant="contained"
              onClick={handleAddShiftSegment}
              disabled={shifts.length >= 10 || shiftOffChecked}
            >
              Add Another Shift
            </Button>
          </Grid>
        </Grid>
      </Grid>
    );
  };

  const renderScheduledDaysOffSection = () => {
    return (
      <Grid container>
        <Grid item xs={12}>
          <Box className={classes.sectionBox} mt={1} mb={1} mr={1}>
            <Typography className={classes.sectionTitleFontStyle}>Scheduled Days Off</Typography>
          </Box>
        </Grid>
        {WEEKDAYS_SHORT.map((day, index) => (
          <Grid item key={`dayCheckGrid-${day}-${index}`} xs={1.5}>
            <FormControl variant="outlined" key={`dayCheck-${day}-${index}`}>
              <FormControlLabel
                checked={offDaysChecked[day]}
                onChange={(event) => handleScheduledDaysOffCheckedChange(event, day)}
                control={<Checkbox />}
                label={day}
                labelPlacement="bottom"
              />
            </FormControl>
          </Grid>
        ))}
        <FormHelperText error={!!scheduledDaysOffErrors} sx={{ marginTop: "10px" }}>
          {scheduledDaysOffErrors}
        </FormHelperText>
      </Grid>
    );
  };

  const renderContinuousWithPreferences = () => {
    return CONTINUOUS_WITH_PREFERENCES_IAM.map((preference) => {
      return (
        <MenuItem key={preference} value={preference}>
          {preference}
        </MenuItem>
      );
    });
  };

  const renderContinuousWithPreferencesSection = () => {
    return (
      <Grid container>
        <Grid item xs={12}>
          <Typography className={classes.sectionTitleFontStyle}>Continuous With Preferences</Typography>{" "}
          <Typography className={classes.sectionTitleDescriptionFontStyle}>- ext. up to 4 hours</Typography>
        </Grid>
        <Grid item xs={12}>
          <Box className={classes.sectionBox} mt={1} mb={1}>
            <FormControl variant="outlined" fullWidth>
              <InputLabel
                id="continuousWithLabel"
                sx={{ marginTop: continuousWith ? "0px" : "-8px" }}
                error={!!continuousWithError}
              >
                Continuous With
              </InputLabel>
              <Select
                size="small"
                labelId="continuousWithLabel"
                id="continuousWithSelectElement"
                value={continuousWith}
                onChange={handleContinuousWithPreferenceChange}
                label="Continuous With"
                error={!!continuousWithError}
              >
                <MenuItem value="" disabled>
                  <em>Continuous With</em>
                </MenuItem>
                {renderContinuousWithPreferences()}
              </Select>
              <FormHelperText error>{continuousWithError}</FormHelperText>
            </FormControl>
            <FormControl>
              <FormControlLabel
                checked={continuousWithTouchingChecked}
                onChange={(event) => setContinuousWithTouchingChecked(event.target.checked)}
                control={<Checkbox />}
                label="Touching a shift"
                disabled={continuousWith === ""}
              />
            </FormControl>
          </Box>
        </Grid>
      </Grid>
    );
  };

  const renderCalloutOTHoursPreferences = () => {
    return CALLOUT_OT_HOURS_PREFERENCES_LIST.map((preference, index) => {
      return (
        <MenuItem key={`preference-${preference}-${index}`} value={preference}>
          {preference}
        </MenuItem>
      );
    });
  };

  const renderCalloutOTTimeOfDayPreferences = () => {
    return CALLOUT_OT_TIME_OF_DAY_PREFERENCES_LIST.map((preference, index) => {
      return (
        <MenuItem key={`preference-${preference}-${index}`} value={preference}>
          {preference}
        </MenuItem>
      );
    });
  };

  const renderCalloutOTPreferencesSection = () => {
    return (
      <Grid container>
        <Grid item xs={12}>
          <Typography className={classes.sectionTitleFontStyle}>Callout OT Preferences</Typography>
          <Typography className={classes.sectionTitleDescriptionFontStyle}>- more than 4 hours</Typography>
        </Grid>
        <Grid item xs={12}>
          <Typography className={classes.sectionSubtitleFontStyle}>Rank the number of hours you prefer:</Typography>
        </Grid>
        {CALLOUT_OT_HOURS_PREFERENCES_LIST.map((preference, index) => {
          if (index === 0 || calloutHours[index - 1].preference !== "") {
            return (
              <Grid container key={`calloutHourPreference-${index + 1}`}>
                <Grid item xs={1}>
                  <Box className={classes.sectionBox} mt={1} mb={1}>
                    <Typography className={classes.listFontStyle}>{index + 1}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box className={classes.sectionBox} mt={1} mb={1}>
                    <FormControl variant="outlined">
                      <InputLabel
                        id={`calloutHourPreference-${index + 1}Label`}
                        sx={{ marginTop: calloutHours[index].preference ? "0px" : "-8px" }}
                        error={!!calloutHours[index].error}
                      >
                        # of Hours
                      </InputLabel>
                      <Select
                        size="small"
                        sx={{ width: "160px" }}
                        labelId={`calloutHourPreference-${index + 1}Label`}
                        id={`calloutHourPreference-${index + 1}SelectElement`}
                        value={calloutHours[index].preference}
                        onChange={(event) => handleCalloutHourPreferenceChange(event, index)}
                        label="# of Hours"
                        error={!!calloutHours[index].error}
                      >
                        <MenuItem value="" disabled>
                          <em># of Hours</em>
                        </MenuItem>
                        {renderCalloutOTHoursPreferences()}
                      </Select>
                    </FormControl>
                  </Box>
                </Grid>
                <FormHelperText error>{calloutHours[index].error}</FormHelperText>
              </Grid>
            );
          }
          return <></>;
        })}
        <Grid item xs={12}>
          <Typography className={classes.sectionSubtitleFontStyle}>Rank the time of day you prefer:</Typography>
        </Grid>
        {CALLOUT_OT_TIME_OF_DAY_PREFERENCES_LIST.map((preference, index) => {
          if (index === 0 || calloutTimeOfDayPreferences[index - 1].preference !== "") {
            return (
              <Grid container key={`calloutTimePreference-${index + 1}`}>
                <Grid item xs={1}>
                  <Box className={classes.sectionBox} mt={1} mb={1}>
                    <Typography className={classes.listFontStyle}>{index + 1}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box className={classes.sectionBox} mt={1} mb={1}>
                    <FormControl variant="outlined">
                      <InputLabel
                        id={`calloutTimePreference-${index + 1}Label`}
                        sx={{ marginTop: calloutTimeOfDayPreferences[index].preference ? "0px" : "-8px" }}
                        error={!!calloutTimeOfDayPreferences[index].error}
                      >
                        Time of Day
                      </InputLabel>
                      <Select
                        size="small"
                        sx={{ width: "160px" }}
                        labelId={`calloutTimePreference-${index + 1}Label`}
                        id={`calloutTimePreference-${index + 1}SelectElement`}
                        value={calloutTimeOfDayPreferences[index].preference}
                        onChange={(event) => handleCalloutTimeOfDayPreferenceChange(event, index)}
                        label="Time of Day"
                        error={!!calloutTimeOfDayPreferences[index].error}
                      >
                        <MenuItem value="" disabled>
                          <em>Time of Day</em>
                        </MenuItem>
                        {renderCalloutOTTimeOfDayPreferences()}
                      </Select>
                    </FormControl>
                  </Box>
                </Grid>
                <Grid item xs={5}>
                  <Box className={classes.sectionBox} mt={1} mb={1}>
                    <FormControl variant="outlined">
                      <FormControlLabel
                        checked={calloutTimeOfDayPreferences[index].touchingShift}
                        onChange={(event) => handleCalloutTimeOfDayTouchingShiftCheckedChange(event, index)}
                        control={<Checkbox />}
                        label="Touching a shift"
                        disabled={calloutTimeOfDayPreferences[index].preference === ""}
                      />
                    </FormControl>
                  </Box>
                </Grid>
                <FormHelperText error>{calloutTimeOfDayPreferences[index].error}</FormHelperText>
              </Grid>
            );
          }
          return <></>;
        })}
      </Grid>
    );
  };

  return (
    <div data-testid="create-voluntary-ot-signup">
      <Button
        className={props.isDesktopView ? classes.button : classes.mobileButton}
        variant="contained"
        color="primary"
        onClick={handleClickDialogOpen}
        data-testid="create-new-signup-button"
      >
        Create New Signup
      </Button>

      {isSignupDialogOpen && (
        <Dialog
          className={props.isDesktopView ? "action-check-dialog" : "detail-dialog"}
          data-testid="action-check-dialog"
          fullScreen={!props.isDesktopView}
          fullWidth
          sx={{ flexDirection: "column", maxHeight: "100vh" }}
          maxWidth="md"
          open={isSignupDialogOpen}
          TransitionComponent={SlideUpTransition}
          keepMounted
          onClose={handleClickDialogClose}
          aria-labelledby="alert-dialog-slide-title"
          aria-describedby="alert-dialog-slide-description"
        >
          <DialogTitle id="alert-dialog-slide-title" className="detail-dialog-title">
            {props.station} | {props.workgroup} | {props.selectedDate.toFormat("MM/dd/yyyy")} OT Signup
          </DialogTitle>
          <Box
            sx={{
              bgcolor: "secondary.swaGray2",
              width: "100%",
              minHeight: "50px",
              maxHeight: "50px",
              borderColor: "secondary.swaGray2",
              borderRadius: 0,
            }}
          >
            <Grid container justifyContent="space-between" alignItems="center">
              <Grid item xs={8} sm={8}>
                <Typography sx={{ marginLeft: "10px" }} className={classes.sectionTitleFontStyle}>
                  Seniority Date: {isSeniorityDateLoading ? <CircularProgress size={20} /> : seniorityDate}
                </Typography>
              </Grid>
              <Grid item xs={4} sm={4}>
                <Button
                  style={{ backgroundColor: "white", paddingLeft: 8, paddingRight: 8 }}
                  size="small"
                  sx={{ float: "right", marginTop: "10px", marginRight: "10px" }}
                  onClick={resetForm}
                  variant="outlined"
                >
                  CLEAR ALL
                </Button>
              </Grid>
            </Grid>
          </Box>
          <DialogContent>
            <Grid container sx={{ flexDirection: "column" }}>
              <DialogContentText component={"div"} id="alert-dialog-slide-description">
                {renderPhoneNumberSection()}
                <Divider sx={{ marginTop: "5px", marginBottom: "5px" }} />
                {renderScheduledHoursSection()}
                <Divider sx={{ marginTop: "5px", marginBottom: "5px" }} />
                {renderScheduledDaysOffSection()}
                <Divider sx={{ marginTop: "5px", marginBottom: "5px" }} />
                {renderContinuousWithPreferencesSection()}
                <Divider sx={{ marginTop: "5px", marginBottom: "5px" }} />
                {renderCalloutOTPreferencesSection()}
                <Grid container justifyContent="center">
                  <Grid item xs={12} sx={{ display: "flex", justifyContent: "center" }}>
                    <ConfirmButton variant="outlined" color="primary" onClick={resetPreferencesSections}>
                      Clear Preferences
                    </ConfirmButton>
                  </Grid>
                </Grid>
                <Divider sx={{ marginTop: "5px", marginBottom: "5px" }} />
              </DialogContentText>
            </Grid>
          </DialogContent>
          <DialogActions className="action-check-dialog-buttons">
            <Grid container justifyContent="space-evenly" alignItems="center">
              <Grid item xs={4}>
                <FormControl variant="outlined">
                  <FormControlLabel
                    checked={willAcceptChecked}
                    onChange={(event) => {
                      setWillAcceptChecked(event.target.checked);
                    }}
                    control={<Checkbox />}
                    label="Will Accept"
                  />
                </FormControl>
              </Grid>
              <Grid item xs={5}>
                <FormControl variant="outlined">
                  <FormControlLabel
                    checked={waive45MinChecked}
                    onChange={(event) => {
                      setWaive45MinChecked(event.target.checked);
                    }}
                    control={<Checkbox />}
                    label="Waive 45-Minute Minimum"
                  />
                </FormControl>
              </Grid>

              <Grid container mt={1} mb={1} justifyContent="flex-end" spacing={2}>
                <ConfirmButton variant="outlined" color="primary" onClick={handleClickDialogClose}>
                  Cancel
                </ConfirmButton>
                <div data-testid="submit-signup-button">
                  <ConfirmButton
                    variant="contained"
                    color="primary"
                    onClick={handleOnSubmitClick}
                    isLoading={isSubmitLoading}
                  >
                    Submit
                  </ConfirmButton>
                </div>
              </Grid>
            </Grid>
          </DialogActions>

          <ConfirmationDialog
            isDialogOpen={isSubmitConfirmationDialogOpen}
            message={"Are you sure you want to submit this Signup?"}
            handleNoButtonClicked={() => setIsSubmitConfirmationDialogOpen(false)}
            handleYesButtonClicked={handleSubmitYesClicked}
            isYesLoading={isSubmitLoading}
          />

          <SuccessDialog
            title="Submit"
            isSuccessDialogOpen={isSubmitSuccessDialogOpen}
            message={"Successfully submitted your Voluntary OT Signup"}
            hasError={submitErrorMessage !== ""}
            error={submitErrorMessage}
            handleOKClick={() => {
              setIsSubmitSuccessDialogOpen(false);
              setIsSubmitConfirmationDialogOpen(false);
              if (submitErrorMessage === "") {
                handleClickDialogClose();
              }
            }}
          />
        </Dialog>
      )}
    </div>
  );
  //#endregion
};

CreateVoluntaryOTSignupIAM.propTypes = {
  isDesktopView: PropTypes.bool.isRequired,
  station: PropTypes.string.isRequired,
  workgroup: PropTypes.string.isRequired,
  selectedDate: PropTypes.object.isRequired,
};

export default CreateVoluntaryOTSignupIAM;
